import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Flame, BookMarked, Sun, Mic, CalendarDays, Star } from 'lucide-react';
import { centerToast } from '../components/CenterToast';
import PointsToast from '../components/PointsToast';

const ProgressBar = ({ value, className = 'bg-primary' }) => (
  <div
    className="relative h-2 w-full rounded-full bg-muted overflow-hidden"
    role="progressbar"
    aria-valuenow={value}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <div
      className={`h-full ${className} rounded-full transition-all duration-500`}
      style={{ width: `${value}%` }}
    />
  </div>
);

// Convert JS day index (0=Sun, 1=Mon, ..) into 0=Mon .. 6=Sun order
const todayDayIndexMonFirst = () => {
  const js = new Date().getDay(); // 0..6 (Sun..Sat)
  return (js + 6) % 7; // 0..6 (Mon..Sun)
};

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export default function FaithJourney({ onBack, onNavigate }) {
  const todayIdx = todayDayIndexMonFirst();
  const [khatamJuz, setKhatamJuz] = useState(8);
  const [streak, setStreak] = useState(12);
  // No longer grows: logging worship does not earn points. Kept only so the
  // deprecated layout still renders until this screen is deleted.
  const monthPoints = 85;
  const [salahLogged, setSalahLogged] = useState(() => {
    // Mock: Mon-Wed already logged this week, up to (but not including) today
    const arr = Array(7).fill(false);
    for (let i = 0; i < Math.min(todayIdx, 3); i += 1) arr[i] = true;
    return arr;
  });
  const [pointsToast, setPointsToast] = useState({ visible: false, points: 0, action: '' });

  // Points for self-reported worship are gone (pivot pack §4 F4). Redeemable
  // rewards for something nobody can verify invite false logging and turn
  // ibadah into a transaction; the reward for logging is the chart itself.
  // Points remain for verifiable actions — attendance scans, volunteering
  // hours, giving. This screen is superseded by the tracker (Home › Tracker)
  // and is kept only until Ikram confirms it can be deleted.
  const celebrate = (_points, action) => {
    setPointsToast({ visible: true, points: 0, action });
  };

  const streakPct = Math.min(100, Math.round((streak / 30) * 100));

  const handleLogReading = () => {
    setStreak((s) => s + 1);
    celebrate(5, 'Quran reading logged!');
  };

  const handleLogSalah = () => {
    if (salahLogged[todayIdx]) {
      centerToast("Today's prayers already logged", '🤲');
      return;
    }
    setSalahLogged((prev) => {
      const next = [...prev];
      next[todayIdx] = true;
      return next;
    });
    celebrate(10, 'Today\'s prayers logged!');
  };

  const handleUploadRecitation = () => {
    centerToast('Audio upload coming soon. WhatsApp your recitation to +65 XXXX XXXX for now.', '🎙️');
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={onBack} className="gap-2" data-testid="faith-journey-back">
            <ArrowLeft className="h-4 w-4" />
            My Ihsan
          </Button>
          <h1 className="text-lg sm:text-xl font-semibold">Faith Journey</h1>
          <div className="w-20" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Points earned this month banner */}
        <Card className="bg-amber-50 border-[#D4A843]/40">
          <CardContent className="p-4 flex items-center gap-3">
            <span className="text-2xl leading-none">⭐</span>
            <div className="flex-1">
              <p className="text-sm font-semibold">
                <span data-testid="month-points">{monthPoints}</span> Ihsan Points
              </p>
              <p className="text-xs text-muted-foreground">
                From attending, volunteering and giving — not from logging worship
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card 1: Quran Reading Streak */}
        <Card data-testid="milestone-streak">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-semibold text-base">Daily Quran Reading</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <span data-testid="streak-count">{streak}</span>-day streak
                  <Flame className="h-4 w-4 text-orange-500 fill-orange-500" />
                </p>
              </div>
              <span className="text-xs font-bold text-primary whitespace-nowrap">+5 pts per day</span>
            </div>
            <ProgressBar value={streakPct} />
            <p className="text-xs text-muted-foreground text-right">{streak} of 30 days</p>

            {/* 30-day consistency heatmap (last 30 days, oldest → today) */}
            <div className="pt-1">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                Last 30 days
              </p>
              <div
                data-testid="streak-heatmap"
                className="grid grid-cols-6 gap-1"
              >
                {Array.from({ length: 30 }).map((_, i) => {
                  // The last `streak` cells (rightmost, i.e. most recent) are filled
                  const filled = i >= 30 - Math.min(streak, 30);
                  const isToday = i === 29;
                  return (
                    <div
                      key={i}
                      data-testid={`heatmap-cell-${i}`}
                      aria-label={filled ? 'Logged' : 'Not logged'}
                      className={`aspect-square rounded-[3px] ${
                        filled ? 'bg-primary' : 'bg-muted'
                      } ${isToday ? 'ring-2 ring-[#D4A843]' : ''}`}
                    />
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-1.5 text-[10px] text-muted-foreground">
                <span>30 days ago</span>
                <span>Today</span>
              </div>
            </div>

            <Button
              data-testid="log-reading"
              onClick={handleLogReading}
              className="w-full h-10 text-sm"
            >
              Log today&apos;s reading
            </Button>
          </CardContent>
        </Card>

        {/* Card 2: Khatam Al-Quran */}
        <Card data-testid="milestone-khatam">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-semibold text-base flex items-center gap-2">
                  <BookMarked className="h-4 w-4 text-primary" />
                  Khatam Al-Quran
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {khatamJuz >= 30 ? 'Completed — all 30 Juz' : `In progress — Juz ${khatamJuz} of 30`}
                </p>
              </div>
              <span className="text-xs font-bold text-primary whitespace-nowrap">+200 pts on completion</span>
            </div>
            <ProgressBar value={Math.round((khatamJuz / 30) * 100)} />
            <Button
              variant="outline"
              disabled={khatamJuz >= 30}
              className="w-full h-10 text-sm border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground"
              onClick={() => {
                const next = Math.min(khatamJuz + 1, 30);
                setKhatamJuz(next);
                centerToast(
                  next >= 30 ? 'Khatam complete — Alhamdulillah!' : `Juz ${next} logged`,
                  next >= 30 ? '🎉' : '📖'
                );
              }}
            >
              {khatamJuz >= 30 ? 'Khatam complete' : 'Log next Juz'}
            </Button>
          </CardContent>
        </Card>

        {/* Card 3: Salah Consistency */}
        <Card data-testid="milestone-salah">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-semibold text-base flex items-center gap-2">
                  <Sun className="h-4 w-4 text-amber-500" />
                  5 Daily Prayers
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5">This week</p>
              </div>
              <span className="text-xs font-bold text-primary whitespace-nowrap">+10 pts per full day</span>
            </div>
            <div className="flex items-center justify-between gap-1 py-1">
              {DAY_LABELS.map((label, i) => {
                const isToday = i === todayIdx;
                const logged = salahLogged[i];
                return (
                  <div key={i} className="flex flex-col items-center gap-1 flex-1">
                    <div
                      data-testid={`day-dot-${i}`}
                      className={`relative w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                        logged
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {isToday && !logged && (
                        <span className="absolute inset-0 rounded-full ring-2 ring-primary animate-ping opacity-60" />
                      )}
                      {label}
                    </div>
                    <span className={`text-[9px] ${isToday ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                      {isToday ? 'Today' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
            <Button
              data-testid="log-salah"
              onClick={handleLogSalah}
              className="w-full h-10 text-sm"
            >
              Log today&apos;s prayers
            </Button>
          </CardContent>
        </Card>

        {/* Card 4: Audio Recitation Upload */}
        <Card data-testid="milestone-recitation">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-semibold text-base flex items-center gap-2">
                  <Mic className="h-4 w-4 text-primary" />
                  Surah Recitation Review
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                  Submit a recitation for review by a certified Asatizah
                </p>
              </div>
              <span className="text-xs font-bold text-primary whitespace-nowrap">+50 pts on approval</span>
            </div>
            <div className="text-xs bg-amber-50 border border-[#D4A843]/30 rounded-md px-3 py-2">
              <span className="font-medium">Last submission:</span>{' '}
              <span className="text-muted-foreground">Pending review (3 days)</span>
            </div>
            <Button
              variant="outline"
              data-testid="upload-recitation"
              onClick={handleUploadRecitation}
              className="w-full h-10 text-sm border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground"
            >
              Upload new recitation
            </Button>
          </CardContent>
        </Card>

        {/* Card 5: Islamic Events */}
        <Card data-testid="milestone-events">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-semibold text-base flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  Community Events Attended
                </h3>
              </div>
              <span className="text-xs font-bold text-primary whitespace-nowrap">+30 pts per event</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                Maulid 2026 ✅
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                Khatam Quran Night ✅
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Next:</span> Futuwwah Collective — 6 Sep
            </p>
            <Button
              variant="outline"
              className="w-full h-10 text-sm border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground"
              onClick={() => onNavigate?.('updates')}
            >
              See upcoming events
            </Button>
          </CardContent>
        </Card>

        {/* Total this month */}
        <div className="text-center pt-2">
          <p className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-amber-50 border-2 border-[#D4A843]">
            <Star className="h-4 w-4 text-[#D4A843] fill-[#D4A843]" />
            Total this month: {monthPoints} pts
          </p>
        </div>
      </div>

      <PointsToast
        isVisible={pointsToast.visible}
        points={pointsToast.points}
        action={pointsToast.action}
        onDismiss={() => setPointsToast((p) => ({ ...p, visible: false }))}
      />
    </div>
  );
}
